/**
 * useAuth Hook
 *
 * Authentication hook for route protection and auth state access.
 * Powered by the Zustand auth store with encrypted persistence.
 *
 * @example
 * ```ts
 * const { isAuthenticated, isLoading, user } = useAuth();
 *
 * if (isLoading) return <Spinner />;
 * if (!isAuthenticated) return <Redirect to="/sign-in" />;
 * ```
 */

import { useAuthStore } from '../stores/authStore';

export interface AuthState {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether the auth state is still being determined (e.g., checking token) */
  isLoading: boolean;
  /** The currently authenticated user */
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    emailVerified: boolean;
    avatarUrl?: string | null;
    phone?: string | null;
  } | null;
}

export const useAuth = (): AuthState => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);

  return {
    isAuthenticated,
    isLoading,
    user: user
      ? {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          emailVerified: user.emailVerified,
          avatarUrl: user.avatarUrl,
          phone: user.phone,
        }
      : null,
  };
};